/**
 * Return Report Detail Component
 *
 * Displays the complete details of a booking return report.
 * Used in the Return Info tab of booking details.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  Camera,
  MessageSquare,
  Clock,
  History,
  ClipboardCheck,
  Package,
  ChevronDown,
  ChevronUp,
  Save,
  Edit3,
  X,
} from 'lucide-react';
import {
  getReturnReportByBookingId,
  getReturnVersionHistory,
  markAsReviewed,
  updateInternalNotes,
} from '../services/return-reports.service';
import { useAuth } from '../contexts/AuthContext';
import ReturnPhotoGallery from './ReturnPhotoGallery';
import ReportIssueModal from './ReportIssueModal';
import { useDateFormatter } from '../hooks/useDateFormatter';
import type {
  BookingReturnWithDetails,
  BookingItemReturn,
  ReturnVersionHistory,
} from '../types/return-reports';
import {
  STATUS_CONFIG,
  ITEM_STATUS_CONFIG,
  REVIEW_STATUS_CONFIG,
} from '../types/return-reports';

interface ReturnReportDetailProps {
  bookingId: string;
  locationId?: string | null;
  onReviewStatusChange?: () => void;
}

export default function ReturnReportDetail({
  bookingId,
  locationId,
  onReviewStatusChange,
}: ReturnReportDetailProps) {
  const { t } = useTranslation();
  const { user, merchantId } = useAuth();
  const { formatDateShort, formatDateTimeCompact } = useDateFormatter();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<BookingReturnWithDetails | null>(null);
  const [issueModalItem, setIssueModalItem] = useState<BookingItemReturn | null>(null);
  const [itemIssueIds, setItemIssueIds] = useState<Record<string, string>>({}); // item_return_id -> issue_id
  const [versionHistory, setVersionHistory] = useState<ReturnVersionHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Internal notes editing
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Review status
  const [markingReviewed, setMarkingReviewed] = useState(false);

  useEffect(() => {
    loadData();
  }, [bookingId]);

  async function loadData() {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await getReturnReportByBookingId(bookingId);

    if (fetchError) {
      setError(fetchError);
    } else if (data) {
      setReport(data);
      setNotesValue(data.internal_notes || '');

      // Load version history if there are multiple versions
      if (data.version > 1) {
        const { data: history } = await getReturnVersionHistory(bookingId);
        if (history) {
          setVersionHistory(history);
        }
      }
    }

    setLoading(false);
  }

  const handleMarkAsReviewed = async () => {
    if (!report || !user) return;

    setMarkingReviewed(true);
    const { success, error } = await markAsReviewed(report.id, user.id);

    if (success) {
      setReport({ ...report, review_status: 'reviewed', reviewed_at: new Date().toISOString() });
      onReviewStatusChange?.();
    } else {
      setError(error || t('returnReport.failedToMarkReviewed'));
    }

    setMarkingReviewed(false);
  };

  const handleSaveNotes = async () => {
    if (!report) return;

    setSavingNotes(true);
    const { success, error } = await updateInternalNotes(report.id, notesValue);

    if (success) {
      setReport({ ...report, internal_notes: notesValue });
      setEditingNotes(false);
    } else {
      setError(error || t('returnReport.failedToSaveNotes'));
    }

    setSavingNotes(false);
  };

  const toggleItemExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const formatAnswerKey = (key: string): string => {
    const translated = t(`returnReport.answerKey.${key}`, { defaultValue: '' });
    if (translated) {
      return translated;
    }
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-xl">
        <Loader2 className="w-8 h-8 animate-spin text-blue" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-md bg-danger/10 border border-danger/20 rounded-lg flex items-start gap-md">
        <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-danger">{t('returnReport.errorLoading')}</p>
          <p className="text-sm text-danger/80 mt-xs">{error}</p>
        </div>
      </div>
    );
  }

  // No return report
  if (!report) {
    return (
      <div className="text-center py-xl">
        <Package className="w-12 h-12 text-secondary mx-auto mb-md" />
        <h3 className="text-lg font-medium text-primary mb-sm">{t('returnReport.noReport')}</h3>
        <p className="text-sm text-secondary">
          {t('returnReport.noReportDescription')}
        </p>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[report.overall_status] || {
    labelKey: 'common.unknown',
    color: 'text-secondary',
    bgColor: 'bg-secondary/10',
  };
  const reviewConfig = REVIEW_STATUS_CONFIG[report.review_status || 'pending'] || {
    labelKey: 'common.unknown',
    color: 'text-secondary',
    bgColor: 'bg-secondary/10',
  };

  // Get photos for items
  const getItemPhotos = (itemReturnId: string) => {
    return report.item_photos?.filter((p) => p.booking_item_return_id === itemReturnId) || [];
  };

  return (
    <div className="space-y-lg">
      {/* Summary Card */}
      <div className="p-md bg-secondary/5 rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-md">
          <div className="flex items-center gap-md">
            {/* Status Badge */}
            <span
              className={`inline-flex items-center gap-xs px-md py-sm rounded-full text-sm font-medium ${statusConfig.bgColor} ${statusConfig.color}`}
            >
              {report.overall_status === 'all_ok' ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <AlertTriangle className="w-4 h-4" />
              )}
              {t(statusConfig.labelKey)}
            </span>

            {/* Review Status */}
            <span
              className={`inline-flex items-center px-sm py-xs rounded-full text-xs font-medium ${reviewConfig.bgColor} ${reviewConfig.color}`}
            >
              {t(reviewConfig.labelKey)}
            </span>
          </div>

          <div className="flex items-center gap-sm">
            {/* Mark as Reviewed Button */}
            {report.review_status !== 'reviewed' && (
              <button
                onClick={handleMarkAsReviewed}
                disabled={markingReviewed}
                className="btn btn-primary btn-sm inline-flex items-center gap-xs"
              >
                {markingReviewed ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ClipboardCheck className="w-4 h-4" />
                )}
                {t('returnReport.markAsReviewed')}
              </button>
            )}

            {/* Version History Link */}
            {report.version > 1 && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="btn btn-secondary btn-sm inline-flex items-center gap-xs"
              >
                <History className="w-4 h-4" />
                {t('returnReport.viewHistory', { count: report.version })}
              </button>
            )}
          </div>
        </div>

        {/* Submission Info */}
        <div className="mt-md pt-md border-t border-primary flex flex-wrap gap-md text-sm text-secondary">
          <div className="flex items-center gap-xs">
            <Clock className="w-4 h-4" />
            {t('returnReport.submitted')}: {formatDateTimeCompact(report.created_at)}
          </div>
          {report.version > 1 && (
            <div className="flex items-center gap-xs">
              <History className="w-4 h-4" />
              {t('returnReport.version')} {report.version}
            </div>
          )}
        </div>
      </div>

      {/* Version History Panel */}
      {showHistory && versionHistory.length > 0 && (
        <div className="p-md border border-primary rounded-lg">
          <h4 className="text-sm font-medium text-primary mb-md">{t('returnReport.versionHistory')}</h4>
          <div className="space-y-sm">
            {versionHistory.map((version) => (
              <div
                key={version.id}
                className={`flex items-center justify-between p-sm rounded-lg ${
                  version.is_latest ? 'bg-blue/10' : 'bg-secondary/5'
                }`}
              >
                <div className="flex items-center gap-sm">
                  <span className="text-sm font-medium text-primary">
                    {t('returnReport.version')} {version.version}
                  </span>
                  {version.is_latest && (
                    <span className="text-xs bg-blue text-white px-xs rounded">{t('returnReport.current')}</span>
                  )}
                </div>
                <div className="text-xs text-secondary">
                  {formatDateShort(version.created_at)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Booking-Level Photos */}
      {report.booking_photos && report.booking_photos.length > 0 && (
        <div className="p-md border border-primary rounded-lg">
          <div className="flex items-center gap-sm mb-md">
            <Camera className="w-5 h-5 text-secondary" />
            <h3 className="text-base font-medium text-primary">{t('returnReport.returnPhotos')}</h3>
          </div>
          <ReturnPhotoGallery photos={report.booking_photos} title="" />
        </div>
      )}

      {/* Booking-Level Answers */}
      {report.booking_level_answers && Object.keys(report.booking_level_answers).length > 0 && (
        <div className="p-md border border-primary rounded-lg">
          <div className="flex items-center gap-sm mb-md">
            <MessageSquare className="w-5 h-5 text-secondary" />
            <h3 className="text-base font-medium text-primary">{t('returnReport.returnAnswers')}</h3>
          </div>
          <div className="space-y-sm">
            {Object.entries(report.booking_level_answers).map(([key, value]) => (
              <div key={key} className="flex items-start gap-md py-sm border-b border-primary last:border-0">
                <span className="text-sm font-medium text-secondary min-w-[120px]">
                  {formatAnswerKey(key)}:
                </span>
                <span className="text-sm text-primary">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Item Returns */}
      {report.item_returns && report.item_returns.length > 0 && (
        <div className="p-md border border-primary rounded-lg">
          <div className="flex items-center gap-sm mb-md">
            <Package className="w-5 h-5 text-secondary" />
            <h3 className="text-base font-medium text-primary">{t('returnReport.itemReturns')}</h3>
            <span className="text-sm text-secondary">({t('returnReport.itemsCount', { count: report.item_returns.length })})</span>
          </div>
          <div className="space-y-sm">
            {report.item_returns.map((itemReturn) => {
              const itemStatusConfig = ITEM_STATUS_CONFIG[itemReturn.item_status] || {
                labelKey: 'common.unknown',
                color: 'text-secondary',
                bgColor: 'bg-secondary/10',
              };
              const itemPhotos = getItemPhotos(itemReturn.id);
              const isExpanded = expandedItems.has(itemReturn.id);
              const itemName =
                itemReturn.booking_items?.item_name || 'Unknown Item';
              const itemNumber =
                itemReturn.inventory_items?.item_number || itemReturn.booking_items?.code || '';

              return (
                <div
                  key={itemReturn.id}
                  className="border border-primary rounded-lg overflow-hidden"
                >
                  {/* Item Header */}
                  <button
                    onClick={() => toggleItemExpanded(itemReturn.id)}
                    className="w-full flex items-center justify-between p-md bg-secondary/5 hover:bg-secondary/10 transition-colors"
                  >
                    <div className="flex items-center gap-md">
                      <span className="font-medium text-primary">
                        {itemName}
                        {itemNumber && (
                          <span className="text-secondary font-normal ml-sm">{itemNumber}</span>
                        )}
                      </span>
                      <span
                        className={`inline-flex items-center px-sm py-xs rounded-full text-xs font-medium ${itemStatusConfig.bgColor} ${itemStatusConfig.color}`}
                      >
                        {t(itemStatusConfig.labelKey)}
                      </span>
                      {itemPhotos.length > 0 && (
                        <span className="flex items-center gap-xs text-xs text-secondary">
                          <Camera className="w-3 h-3" />
                          {itemPhotos.length}
                        </span>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-secondary" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-secondary" />
                    )}
                  </button>

                  {/* Item Details */}
                  {isExpanded && (
                    <div className="p-md space-y-md">
                      {/* Item Answers */}
                      {itemReturn.item_level_answers &&
                        Object.keys(itemReturn.item_level_answers).length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-secondary mb-sm">{t('returnReport.answers')}</h5>
                            <div className="space-y-xs">
                              {Object.entries(itemReturn.item_level_answers).map(([key, value]) => (
                                <div key={key} className="flex items-start gap-md text-sm">
                                  <span className="text-secondary min-w-[100px]">
                                    {formatAnswerKey(key)}:
                                  </span>
                                  <span className="text-primary">{value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Item Description */}
                      {itemReturn.description && (
                        <div>
                          <h5 className="text-sm font-medium text-secondary mb-sm">{t('returnReport.description')}</h5>
                          <p className="text-sm text-primary">{itemReturn.description}</p>
                        </div>
                      )}

                      {/* Item Photos */}
                      {itemPhotos.length > 0 && (
                        <div>
                          <ReturnPhotoGallery
                            photos={itemPhotos}
                            title="Photos"
                            itemContext={`${itemName} ${itemNumber || ''}`}
                          />
                        </div>
                      )}

                      {/* Create Issue Button */}
                      <div className="pt-sm border-t border-border">
                        {itemIssueIds[itemReturn.id] ? (
                          <p className="text-xs text-secondary italic">{t('returnReport.issueAlreadyCreated')}</p>
                        ) : (
                          <button
                            onClick={() => setIssueModalItem(itemReturn)}
                            className="btn btn-sm btn-ghost text-warning hover:bg-warning/10"
                          >
                            <AlertTriangle className="w-4 h-4" />
                            {t('returnReport.createIssue')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Description */}
      {report.description && (
        <div className="p-md border border-primary rounded-lg">
          <h3 className="text-base font-medium text-primary mb-sm">{t('returnReport.additionalNotesFromCustomer')}</h3>
          <p className="text-sm text-secondary">{report.description}</p>
        </div>
      )}

      {/* Internal Notes */}
      <div className="p-md border border-primary rounded-lg">
        <div className="flex items-center justify-between mb-sm">
          <h3 className="text-base font-medium text-primary">{t('returnReport.internalNotes')}</h3>
          {!editingNotes && (
            <button
              onClick={() => setEditingNotes(true)}
              className="btn btn-secondary btn-sm inline-flex items-center gap-xs"
            >
              <Edit3 className="w-4 h-4" />
              {t('common.edit')}
            </button>
          )}
        </div>

        {editingNotes ? (
          <div className="space-y-sm">
            <textarea
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              className="input w-full text-sm"
              rows={4}
              placeholder={t('returnReport.internalNotesPlaceholder')}
            />
            <div className="flex items-center gap-sm">
              <button
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="btn btn-primary btn-sm inline-flex items-center gap-xs"
              >
                {savingNotes ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {t('common.save')}
              </button>
              <button
                onClick={() => {
                  setEditingNotes(false);
                  setNotesValue(report.internal_notes || '');
                }}
                className="btn btn-secondary btn-sm inline-flex items-center gap-xs"
              >
                <X className="w-4 h-4" />
                {t('common.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-secondary">
            {report.internal_notes || t('returnReport.noInternalNotes')}
          </p>
        )}
      </div>

      {/* Report Issue Modal for item returns */}
      {issueModalItem && report && (
        <ReportIssueModal
          isOpen={!!issueModalItem}
          onClose={() => setIssueModalItem(null)}
          onIssueCreated={(issue) => {
            if (issue && issueModalItem) {
              setItemIssueIds(prev => ({ ...prev, [issueModalItem.id]: issue.id }));
            }
            setIssueModalItem(null);
          }}
          initialData={{
            merchant_id: merchantId || '',
            location_id: locationId || null,
            inventory_item_id: issueModalItem.inventory_item_id || null,
            product_type_id: (issueModalItem as any).booking_items?.product_type_id || null,
            booking_id: bookingId,
            booking_item_return_id: issueModalItem.id,
            source: 'customer_return',
            status: 'reported',
            title: issueModalItem.description
              ? issueModalItem.description.substring(0, 80)
              : `Issue from return: ${issueModalItem.booking_items?.item_name || 'Item'}`,
            description: issueModalItem.description || '',
            item_label: `${issueModalItem.booking_items?.item_name || 'Item'} ${issueModalItem.inventory_items?.item_number || ''}`.trim(),
          }}
        />
      )}
    </div>
  );
}
